import { Button, Chip, Typography } from '@getgo/chameleon-web-react-wrapper/register';
import { ReactElement, RefObject } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { AddOutlinedIcon } from '@getgo/chameleon-icons/react';
import { useTranslation } from 'react-i18next';
import styles from './JobHistoryFilterHeader.module.scss';
import { AutocompleteComponent } from '@getgo/chameleon-web';

interface Props {
  columnFilters: ColumnFiltersState;
  setColumnFilters: (filters: ColumnFiltersState) => void;
  setIsFilterPanelOpen: (isOpen: boolean) => void;
  autocompleteRef: RefObject<AutocompleteComponent>;
}

export default function JobHistoryFilterHeader({
  columnFilters,
  setColumnFilters,
  setIsFilterPanelOpen,
  autocompleteRef
}: Props): ReactElement {
  const { t } = useTranslation();

  function translatedFilterValue(columnId: string, values: string[] | string): string | undefined {
    if (typeof values === 'string') {
      return values as string;
    }
    const translatedValues = values.map(value => {
      if (columnId === 'updateCount') {
        return t<any>(`jobHistoryGrid.updates.${value}`);
      }
      if (columnId === 'tenants') {
        return value;
      }
      return value;
    });
    if (values.length < 4) return translatedValues.join(', ');
    else {
      return `${translatedValues.slice(0, 3).join(', ')} +${values.length - 3}`;
    }
  }

  function isFilterClear(): boolean {
    return columnFilters.every(f => !f.value);
  }

  function FilterButton(): ReactElement {
    if (isFilterClear()) {
      return (
        <Button
          className={styles.filterButton}
          variant="tertiary"
          leadingIcon={<AddOutlinedIcon />}
          onClick={() => setIsFilterPanelOpen(true)}>
          {t('jobHistoryGrid.addFilter')}
        </Button>
      );
    }

    return (
      <Button className={styles.filterButton} variant="tertiary" onClick={() => setIsFilterPanelOpen(true)}>
        {t('jobHistoryGrid.openFilter')}
      </Button>
    );
  }

  return (
    <>
      <FilterButton></FilterButton>
      {columnFilters.map(filter => {
        if (Array.isArray(filter.value)) {
          return (
            <Chip
              key={filter.id}
              className={styles.filterChip}
              closable
              onClose={() => {
                setColumnFilters(columnFilters.filter(f => filter.id !== f.id));
                if (filter.id === 'tenants') {
                  (autocompleteRef.current as AutocompleteComponent).selectedOptions = [];
                }
              }}>
              <Typography>
                {t<any>(`jobHistoryGrid.headers.${filter.id}`)}:{' '}
                <span style={{ fontWeight: 'bold' }}>{translatedFilterValue(filter.id, filter.value)}</span>
              </Typography>
            </Chip>
          );
        }

        return (
          <Chip
            key={filter.id}
            className={styles.filterChip}
            closable
            onClose={() => setColumnFilters(columnFilters.filter(f => filter.id !== f.id))}>
            <Typography>
              {t<any>(`jobHistoryGrid.headers.${filter.id}`)}:{' '}
              <span style={{ fontWeight: 'bold' }}>{translatedFilterValue(filter.id, filter.value as string)}</span>
            </Typography>
          </Chip>
        );
      })}
    </>
  );
}
